import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * theme-customization B1 — THE FIXED DARK INSET FOCUS INDICATOR for the portal fills.
 *
 * The base ring below is `ring-ring` over `ring-offset-2` / `ring-offset-background`, so the
 * editable ring is drawn OUTSIDE the button with the offset band between it and the fill: it
 * never touches the accent, and the surrounds it does touch are measured by the `--ring over
 * <surface>` pairs. What the shared inventory's `--ring on the <accent> fill` records measure is
 * this band — a fixed `--on-accent` inset edge drawn ON the fill — because the editable ring
 * cannot serve there in both modes (dark `--ring` is #FFFFFF, 1.41:1 on the admin yellow).
 * `--on-accent` is code-owned and identical in both modes, and already clears 4.5 on all four
 * accents as the variants' text colour, so this band is visible in light and dark alike.
 *
 * An arbitrary `shadow-[…]` lands in Tailwind's `--tw-shadow` slot while the ring occupies
 * `--tw-ring-shadow`; both are composed into one `box-shadow`, so the two bands render together.
 */
const PORTAL_FOCUS_INSET = 'focus-visible:shadow-[inset_0_0_0_2px_hsl(var(--on-accent-hsl))]';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Portal-specific variants.
        // Fills use dark text (#111827) on ALL FOUR portal
        // accents: white text on #4DABF7 (2.51:1) and #FF6B9D (2.69:1) fails
        // WCAG 1.4.3 (audit 2026-07-01 #3). concept-authority-hardening
        // remediation v2, Phase 5 (CAR-10) — the claim this comment used to
        // make for Agent ("keeps white text, passes as a dark-enough fill")
        // was never measured and was FALSE: white on #A78BFA measures
        // 2.72:1, the same class of failure as Client/Influencer, not a
        // "dark enough fill". #111827 clears 4.5:1 on all four. The accent
        // tokens themselves (`--ss-yellow`/`--ss-blue`/`--ss-pink`/
        // `--ss-purple`) are unchanged — only the on-fill text colour.
        //
        // theme-customization A2a — THE FOREGROUND IS ITS OWN ROLE NOW.
        // This was `text-content-primary`, i.e. `--text-primary`, i.e. the editable
        // `text-strong` role — the same token that paints body headings on a card. Those two
        // duties are not the same pair: one is measured against a card, the other against a
        // brand fill the same edit does not move, so an admin darkening headings would have
        // silently moved a colour whose only job is to stay readable on four accents it does
        // not control. `text-on-accent` is a code-owned foreground holding the identical
        // #111827, so nothing repaints; what changes is that nobody can move it by accident.
        admin: `bg-portal-admin text-on-accent hover:bg-portal-admin/90 ${PORTAL_FOCUS_INSET}`,
        client: `bg-portal-client text-on-accent hover:bg-portal-client/90 ${PORTAL_FOCUS_INSET}`,
        influencer: `bg-portal-influencer text-on-accent hover:bg-portal-influencer/90 ${PORTAL_FOCUS_INSET}`,
        agent: `bg-portal-agent text-on-accent hover:bg-portal-agent/90 ${PORTAL_FOCUS_INSET}`,
      },
      size: {
        default: 'h-8 px-4 py-1 [@media(pointer:coarse)]:min-h-[44px]',
        sm: 'h-8 rounded-md px-3 [@media(pointer:coarse)]:min-h-[44px]',
        lg: 'h-8 rounded-md px-8 [@media(pointer:coarse)]:min-h-[44px]',
        icon: 'h-8 w-8 [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
